import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "tp-button shrink-0 outline-none aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "tp-button--primary",
        destructive: "tp-button--critical",
        outline: "",
        secondary: "",
        ghost: "!border-transparent !bg-transparent",
        link: "!min-h-0 !border-0 !bg-transparent !p-0 text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "",
        xs: "!min-h-6 h-6 !px-2 !text-[length:var(--text-caption)] [&_svg:not([class*='size-'])]:size-3",
        sm: "!min-h-7 h-7 !px-2 !text-[length:var(--text-label)]",
        lg: "!min-h-10 h-10 !px-4",
        icon: "!min-h-[var(--control-height)] size-[var(--control-height)] !p-0",
        "icon-xs": "!min-h-6 size-6 !p-0 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "!min-h-7 size-7 !p-0",
        "icon-lg": "!min-h-10 size-10 !p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
